import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import MapIcon from '@mui/icons-material/Map';
import TuneIcon from '@mui/icons-material/Tune';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AuthContext } from '../../context/AuthContext';
import { mapMappings, currentSeason, admins } from '../../Constants';
import { getDivisionStandingsAll, getMatchById } from '../../services/MatchService';
import {
    getMatchesForAnalysis,
    getMapMatchCounts,
    searchPlayers,
    getPlayerMapMatchCounts,
    getMatchesForPlayerAnalysis,
    AnalysisMatch,
    PlayerSearchResult,
} from '../../services/AnalysisService';
import { getUploadMatch, getMyDemos, UploadedDemo } from '../../services/UserDemoService';
import { checkReplayAvailability } from '../../services/ReplayService';
import { teamWonRound } from '../MatchPage2/matchStats';
import { teamSideInRound } from './roundSide';
import { Standing } from '../../models/Standing';
import { Team } from '../../models/Team';
import { Match } from '../../models/Match';
import PlayerImage from '../../components/PlayerImage';
import { mapPhotoMappings } from './mapPhotos';
import { parseMatchTime } from '../../utils/matchTime';
import styles from './AnalysisPage.module.css';

type BuyTypeFilter = 'pistol' | 'eco' | 'semi-eco' | 'semi-buy' | 'full-buy';
type Mode = 'team' | 'player';

interface FilterState {
    mode: Mode;
    teamId: string;
    playerSteamId: string;
    mapName: string;
    seasons: number[];
    side: 'all' | 'T' | 'CT';
    teamBuyTypes: Set<BuyTypeFilter>;
    opponentBuyTypes: Set<BuyTypeFilter>;
    outcome: 'all' | 'W' | 'L';
}

interface FilteredRound {
    gameId: string;
    matchId: string;
    roundNumber: number;
    season: number;
    teamId: string;
    // Private uploads: rounds in one analysis can come from several demos, so each round
    // carries the upload its replay lives under.
    uploadId?: string;
}

const MAPS = Array.from(mapMappings.entries());
const BUY_TYPES: { id: BuyTypeFilter; label: string }[] = [
    { id: 'pistol', label: 'Pistol' },
    { id: 'eco', label: 'Eco' },
    { id: 'semi-eco', label: 'Semi Eco' },
    { id: 'semi-buy', label: 'Semi Buy' },
    { id: 'full-buy', label: 'Full Buy' },
];

// A private upload lives outside the league seasons and is invisible to /Analysis/*, so its
// one match is built here from the owner-scoped /UserDemo endpoints instead of fetched.
const uploadToAnalysisMatch = (m: Match, roundCount: number, uploadId: string): AnalysisMatch => ({
    uploadId,
    gameid: m.gameid,
    matchid: m.matchid,
    mapName: m.mapName.replace('*', ''),
    season: m.season,
    division: m.division,
    team1Id: m.team1?.teamId ?? '',
    team1Name: m.team1?.teamName ?? '',
    team1Logo: m.team1?.teamLogo ?? '',
    team2Id: m.team2?.teamId ?? '',
    team2Name: m.team2?.teamName ?? '',
    team2Logo: m.team2?.teamLogo ?? '',
    team1playerids: m.team1playerids ?? [],
    team2playerids: m.team2playerids ?? [],
    team1score: m.team1score,
    team2score: m.team2score,
    team1won: m.team1won,
    matchTime: m.match_time,
    hasReplay: roundCount > 0,
    roundCount,
    roundEnds: m.roundEnds,
});

const AnalysisFilterPage: React.FC = () => {
    const navigate = useNavigate();
    const { authLoaded, user } = useContext(AuthContext);
    const isAdmin = user && admins.includes(user.steamID);

    // How many games can be compared at once. Donators (and admins) get the full quota; everyone
    // else gets a free taste. Enforced on selection + defensively on submit.
    const isDonator = !!(user?.donator || user?.teamTier || isAdmin);
    const maxGames = isDonator ? 100 : 2;

    // The match page's "Analyse" tab links here as /analyse?match=<matchId>&map=<map>, the
    // prematch prep page as /analyse?team=<teamId>&games=<id,id>&seasons=<n,n>, and the private
    // demo viewer as /analyse?upload=<uploadId>[&token=<shareToken>]. Read once: after this the
    // filters own the truth and the link params are ignored.
    const [searchParams] = useSearchParams();
    const seed = useRef({
        matchId: searchParams.get('match') ?? '',
        teamId: searchParams.get('team') ?? '',
        uploadId: searchParams.get('upload') ?? '',
        shareToken: searchParams.get('token') ?? '',
        // Seasons the caller's games span, so the first load covers all of them rather than
        // just the current one.
        seasons: (searchParams.get('seasons') ?? '')
            .split(',')
            .map(Number)
            .filter(n => Number.isFinite(n) && n > 0),
        // Only a map this build knows about; anything else (a '*' no-demo suffix, a retired map)
        // would leave the map stage stuck on a name the pickers can't show.
        mapName: mapMappings.has((searchParams.get('map') ?? '').replace('*', ''))
            ? (searchParams.get('map') ?? '').replace('*', '')
            : '',
    });

    // Upload mode: the subject is one private demo, so the season and map are fixed and the
    // league pickers (team dropdown, season chips, map tiles) have nothing to offer.
    const isUpload = !!seed.current.uploadId;

    // Which match the user came from, if any. Both of its teams are offered as the subject rather
    // than one being guessed, and it gives us a link back to where they were.
    const [fromMatch, setFromMatch] = useState<Match[] | null>(null);
    // Every demo this user owns, so one analysis can span several of them. Only loaded for the
    // owner — a share token unlocks exactly one upload, so that viewer stays single-demo.
    const [myDemos, setMyDemos] = useState<UploadedDemo[]>([]);

    const [teams, setTeams] = useState<Team[]>([]);
    const [matches, setMatches] = useState<AnalysisMatch[]>([]);
    const [selectedMatchIds, setSelectedMatchIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [mapCounts, setMapCounts] = useState<Record<string, number>>({});
    // Games the caller already chose (the prep page's selection). Applied to the first load of
    // the match list and then cleared, so every later filter change auto-selects as usual.
    const seededGames = useRef<string[]>(
        (searchParams.get('games') ?? '').split(',').filter(Boolean)
    );

    const [filters, setFilters] = useState<FilterState>({
        mode: 'team',
        teamId: seed.current.teamId,
        playerSteamId: '',
        mapName: seed.current.mapName,
        seasons: seed.current.seasons.length ? seed.current.seasons : [currentSeason],
        side: 'all',
        teamBuyTypes: new Set(),
        opponentBuyTypes: new Set(),
        outcome: 'all',
    });

    const [playerResults, setPlayerResults] = useState<PlayerSearchResult[]>([]);
    const [selectedPlayerInfo, setSelectedPlayerInfo] = useState<PlayerSearchResult | null>(null);
    const [playerSearch, setPlayerSearch] = useState('');
    const [playerDropdownOpen, setPlayerDropdownOpen] = useState(false);
    const playerDropdownRef = useRef<HTMLDivElement>(null);

    const [teamSearch, setTeamSearch] = useState('');
    const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
    const teamDropdownRef = useRef<HTMLDivElement>(null);

    // ===== Upload mode: which of my demos belong to the picked team =====

    // The picked side's name. Upload teams are matched by name as well as id below, so this is
    // the key that groups demos of the same team together.
    const uploadTeamName = useMemo((): string => {
        if (!isUpload || !fromMatch || !filters.teamId) return '';
        const t = fromMatch.flatMap(m => [m.team1, m.team2]).find(x => x?.teamId === filters.teamId);
        return t?.teamName ?? '';
    }, [isUpload, fromMatch, filters.teamId]);

    // My completed demos featuring that team, from the /mine payload alone — it already carries
    // status, mapName and both team names, so no per-demo request is needed to shortlist.
    const demosForTeam = useMemo((): UploadedDemo[] => {
        if (!uploadTeamName) return [];
        return myDemos.filter(d => d.team1Name === uploadTeamName || d.team2Name === uploadTeamName);
    }, [myDemos, uploadTeamName]);

    // Pull in the match behind ?match=, and take its season so the pickers below cover it. Runs
    // once on mount; from then on the season selector is the user's.
    useEffect(() => {
        if (isUpload || !seed.current.matchId) return;
        getMatchById(seed.current.matchId)
            .then(maps => {
                if (!maps || maps.length === 0 || !maps[0].team1 || !maps[0].team2) return;
                setFromMatch(maps);
                setFilters(f => ({ ...f, seasons: [maps[0].season] }));
            })
            .catch(() => setFromMatch(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Same, for ?upload=. The owner-scoped endpoint returns the identical Match[] shape, so the
    // "from match" banner below offers both sides exactly as it does for a league match. The
    // season is always 9999; the map comes from the demo but is re-pickable once we know which
    // other demos exist.
    useEffect(() => {
        if (!isUpload) return;
        const token = seed.current.shareToken || undefined;
        getUploadMatch(seed.current.uploadId, token)
            .then(maps => {
                if (!maps || maps.length === 0 || !maps[0].team1 || !maps[0].team2) return;
                setFromMatch(maps);
                setFilters(f => ({
                    ...f,
                    mode: 'team',
                    seasons: [maps[0].season],
                    mapName: maps[0].mapName.replace('*', ''),
                }));
            })
            .catch(() => setFromMatch(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The rest of this user's demos, so a team with several uploads can be analysed across all
    // of them. Skipped for share-link viewers: their token grants one upload, and /mine needs a
    // JWT they don't have.
    useEffect(() => {
        if (!isUpload || seed.current.shareToken) return;
        getMyDemos()
            .then(d => setMyDemos(d.filter(x => x.status === 'completed')))
            .catch(() => setMyDemos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Picking a side from that match is the same as choosing it in the Subject card, except we
    // already hold the real Team object, logo and all.
    const pickMatchTeam = (team: Team) => {
        setFilters(f => ({ ...f, mode: 'team', playerSteamId: '', teamId: team.teamId }));
        setSelectedPlayerInfo(null);
        setTeamSearch('');
        setTeamDropdownOpen(false);
    };

    useEffect(() => {
        // Season 9999 has no division standings — the two upload teams come from the demo.
        if (isUpload) return;
        const loadTeams = async () => {
            try {
                const seasonForTeams = filters.seasons.length > 0
                    ? Math.max(...filters.seasons)
                    : currentSeason;
                const divisionMap: Record<string, Standing[]> = await getDivisionStandingsAll(seasonForTeams);
                const uniqueTeams = new Map<string, Team>();
                Object.values(divisionMap).forEach(standings => {
                    if (!Array.isArray(standings)) return;
                    standings.forEach(s => {
                        if (s.team && !uniqueTeams.has(s.team.teamId)) {
                            uniqueTeams.set(s.team.teamId, s.team);
                        }
                    });
                });
                setTeams(Array.from(uniqueTeams.values()).sort((a, b) => a.teamName.localeCompare(b.teamName)));
            } catch {
                setTeams([]);
            }
        };
        loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.seasons.join(',')]);

    useEffect(() => {
        if (filters.mode !== 'player') return;
        if (!playerSearch || playerSearch.length < 2) { setPlayerResults([]); return; }
        const handle = setTimeout(async () => {
            const results = await searchPlayers(playerSearch);
            setPlayerResults(results);
        }, 200);
        return () => clearTimeout(handle);
    }, [playerSearch, filters.mode]);

    useEffect(() => {
        // Upload mode counts my own demos per map instead of asking /Analysis/map-counts, which
        // filters uploads out. This drives the same map card the league flow uses.
        if (isUpload) {
            const counts: Record<string, number> = {};
            for (const d of demosForTeam) {
                const key = (d.mapName ?? '').replace('*', '');
                if (key) counts[key] = (counts[key] ?? 0) + 1;
            }
            setMapCounts(counts);
            return;
        }
        if (filters.mode === 'team') {
            if (!filters.teamId) { setMapCounts({}); return; }
            getMapMatchCounts(filters.teamId, filters.seasons).then(setMapCounts);
        } else {
            if (!filters.playerSteamId) { setMapCounts({}); return; }
            getPlayerMapMatchCounts(filters.playerSteamId, filters.seasons).then(setMapCounts);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.mode, filters.teamId, filters.playerSteamId, filters.seasons.join(','), demosForTeam]);

    useEffect(() => {
        const hasSubject = filters.mode === 'team' ? !!filters.teamId : !!filters.playerSteamId;
        if (!hasSubject || !filters.mapName) {
            setMatches([]);
            return;
        }
        if (isUpload) {
            // /Analysis/matches filters uploads out, so the list is assembled from my own demos:
            // every completed upload featuring this team on this map, not just the one I opened.
            // A share-token viewer only ever has access to the single upload the token unlocks.
            const uploadIds = seed.current.shareToken
                ? [seed.current.uploadId]
                : demosForTeam
                    .filter(d => (d.mapName ?? '').replace('*', '') === filters.mapName)
                    .map(d => d.uploadId);
            if (uploadIds.length === 0) { setMatches([]); setSelectedMatchIds(new Set()); return; }

            let cancelled = false;
            (async () => {
                setLoading(true);
                const token = seed.current.shareToken || undefined;
                const built = (await Promise.all(uploadIds.map(async uploadId => {
                    try {
                        const maps = await getUploadMatch(uploadId, token);
                        return await Promise.all((maps ?? [])
                            .filter(m => m.team1 && m.team2 && m.mapName.replace('*', '') === filters.mapName)
                            .map(async m => {
                                const { roundCount } = await checkReplayAvailability(m.gameid);
                                return uploadToAnalysisMatch(m, roundCount, uploadId);
                            }));
                    } catch {
                        return [] as AnalysisMatch[];
                    }
                }))).flat();
                if (cancelled) return;
                built.sort((a, b) => parseMatchTime(b.matchTime).getTime() - parseMatchTime(a.matchTime).getTime());
                setMatches(built);
                setSelectedMatchIds(new Set(built
                    .filter(m => m.hasReplay)
                    .slice(0, maxGames)
                    .map(m => m.gameid)));
                setLoading(false);
            })();
            return () => { cancelled = true; };
        }
        const loadMatches = async () => {
            setLoading(true);
            const result = filters.mode === 'team'
                ? await getMatchesForAnalysis(filters.teamId, filters.mapName, filters.seasons)
                : await getMatchesForPlayerAnalysis(filters.playerSteamId, filters.mapName, filters.seasons);
            setMatches(result);
            const playable = result
                .filter(m => m.hasReplay && (filters.mode === 'team' || m.playerTeamId));
            // A caller that already chose its games keeps them; otherwise auto-select up to the
            // tier cap (free = 2, donator/admin = 100).
            const wanted = seededGames.current;
            seededGames.current = [];
            const seeded = wanted.length
                ? playable.filter(m => wanted.indexOf(m.gameid) !== -1)
                : [];
            // A stale or mistyped link matches nothing; fall back to the normal auto-select
            // rather than dropping the user on an empty selection.
            const chosen = seeded.length ? seeded : playable;
            setSelectedMatchIds(new Set(chosen.slice(0, maxGames).map(m => m.gameid)));
            setLoading(false);
        };
        loadMatches();
    // demosForTeam is in the deps because a side can be picked before /mine has come back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.mode, filters.teamId, filters.playerSteamId, filters.mapName, filters.seasons.join(','), demosForTeam]);

    const getPerspectiveTeamId = (match: AnalysisMatch): string => {
        if (filters.mode === 'player') return match.playerTeamId ?? '';
        if (isUpload) {
            // Across several demos the same team is usually one stable id
            // (upload_<owner>_<slug>), but a demo whose teams were never named keeps a
            // per-match synthetic id — so fall back to matching on the name.
            if (match.team1Id === filters.teamId || match.team2Id === filters.teamId) return filters.teamId;
            if (uploadTeamName) {
                if (match.team1Name === uploadTeamName) return match.team1Id;
                if (match.team2Name === uploadTeamName) return match.team2Id;
            }
            return '';
        }
        return filters.teamId;
    };

    const filteredRounds = useMemo(() => {
        const rounds: FilteredRound[] = [];

        for (const match of matches) {
            if (!selectedMatchIds.has(match.gameid)) continue;
            if (!match.roundEnds) continue;

            const perspectiveTeamId = getPerspectiveTeamId(match);
            if (!perspectiveTeamId) continue;

            for (const [roundStr, roundEnd] of Object.entries(match.roundEnds)) {
                const roundNumber = parseInt(roundStr);

                // No replays are loaded yet, so this reads the round end's T-side team and only
                // falls back to the round number for rounds parsed before S32.
                const teamSide = teamSideInRound(match, perspectiveTeamId, roundNumber);
                const teamIsCT = teamSide === 'CT';

                if (filters.side !== 'all' && filters.side !== teamSide) continue;

                if (filters.teamBuyTypes.size > 0) {
                    const teamBuyType = teamIsCT ? roundEnd.ctBuyType : roundEnd.tBuyType;
                    if (teamBuyType && !filters.teamBuyTypes.has(teamBuyType as BuyTypeFilter)) continue;
                }

                if (filters.opponentBuyTypes.size > 0) {
                    const oppBuyType = teamIsCT ? roundEnd.tBuyType : roundEnd.ctBuyType;
                    if (oppBuyType && !filters.opponentBuyTypes.has(oppBuyType as BuyTypeFilter)) continue;
                }

                if (filters.outcome !== 'all') {
                    // Uploaded demos may have no round-end team id at all (the parser resolves
                    // it by team name before the synthetic team exists) or a stale one (naming the
                    // teams re-keys the match but not the round ends), so teamWonRound falls back
                    // to the reason in both cases.
                    const teamWon = teamWonRound(
                        roundEnd.reason,
                        roundEnd.teamWonID,
                        perspectiveTeamId,
                        match.team1Id === perspectiveTeamId ? match.team2Id : match.team1Id,
                        teamIsCT,
                    );
                    if (filters.outcome === 'W' && !teamWon) continue;
                    if (filters.outcome === 'L' && teamWon) continue;
                }

                rounds.push({
                    gameId: match.gameid,
                    matchId: match.matchid,
                    roundNumber,
                    season: match.season,
                    teamId: perspectiveTeamId,
                    uploadId: match.uploadId,
                });
            }
        }
        return rounds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matches, selectedMatchIds, filters]);

    const roundsPerMatch = useMemo(() => {
        const counts: Record<string, number> = {};
        filteredRounds.forEach(r => {
            counts[r.gameId] = (counts[r.gameId] || 0) + 1;
        });
        return counts;
    }, [filteredRounds]);

    const toggleMatch = (gameId: string) => {
        setSelectedMatchIds(prev => {
            const next = new Set(prev);
            if (next.has(gameId)) {
                next.delete(gameId);
            } else {
                if (next.size >= maxGames) return prev; // at the tier cap — ignore further adds
                next.add(gameId);
            }
            return next;
        });
    };

    const toggleAllMatches = () => {
        const selectable = matches.filter(m => m.hasReplay && (filters.mode === 'team' || m.playerTeamId));
        const cappedCount = Math.min(selectable.length, maxGames);
        if (selectedMatchIds.size >= cappedCount) {
            setSelectedMatchIds(new Set());
        } else {
            // Select up to the tier cap.
            setSelectedMatchIds(new Set(selectable.slice(0, maxGames).map(m => m.gameid)));
        }
    };

    const toggleBuyType = (set: 'team' | 'opponent', buyType: BuyTypeFilter) => {
        setFilters(prev => {
            const key = set === 'team' ? 'teamBuyTypes' : 'opponentBuyTypes';
            const next = new Set(prev[key]);
            if (next.has(buyType)) next.delete(buyType);
            else next.add(buyType);
            return { ...prev, [key]: next };
        });
    };

    // The dropdown only knows teams that appear in a division's standings, so a side picked from a
    // qualifier match can be missing from it. The match carries the real Team object, so prefer
    // that over rendering a blank subject next to counts that are already correct.
    const selectedTeam = useMemo((): Team | undefined => {
        const known = teams.find(t => t.teamId === filters.teamId);
        if (known || !filters.teamId) return known;
        return fromMatch
            ?.flatMap(m => [m.team1, m.team2])
            .find(t => t?.teamId === filters.teamId);
    }, [teams, filters.teamId, fromMatch]);

    const filteredTeams = useMemo(() => {
        if (!teamSearch) return teams;
        const q = teamSearch.toLowerCase();
        return teams.filter(t => t.teamName.toLowerCase().includes(q));
    }, [teams, teamSearch]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (teamDropdownRef.current && !teamDropdownRef.current.contains(e.target as Node)) {
                setTeamDropdownOpen(false);
            }
            if (playerDropdownRef.current && !playerDropdownRef.current.contains(e.target as Node)) {
                setPlayerDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const setMode = (mode: Mode) => {
        setFilters(prev => ({
            ...prev,
            mode,
            teamId: '',
            playerSteamId: '',
            mapName: '',
        }));
        setMatches([]);
        setSelectedMatchIds(new Set());
        setMapCounts({});
        setTeamSearch('');
        setPlayerSearch('');
        setSelectedPlayerInfo(null);
    };

    const handleAnalyse = () => {
        if (filteredRounds.length === 0) return;

        let teamName = '';
        let teamLogo = '';
        if (filters.mode === 'team') {
            teamName = selectedTeam?.teamName || '';
            teamLogo = selectedTeam?.teamLogo || '';
        } else {
            teamName = selectedPlayerInfo?.playerName || '';
        }

        // Defense-in-depth: never let the view receive more than the tier allows.
        const cappedIds = Array.from(selectedMatchIds).slice(0, maxGames);
        const cappedSet = new Set(cappedIds);

        const analysisState = {
            filters,
            mode: filters.mode,
            searchedPlayerSteamId: filters.mode === 'player' ? filters.playerSteamId : undefined,
            selectedMatchIds: cappedIds,
            matches: matches.filter(m => cappedSet.has(m.gameid)),
            filteredRounds: filteredRounds.filter(r => cappedSet.has(r.gameId)),
            teamName,
            teamLogo,
            // Present only for a private upload: the view page must pull its replay rounds
            // through the access-controlled /UserDemo endpoint instead of the public one.
            upload: isUpload
                ? { uploadId: seed.current.uploadId, shareToken: seed.current.shareToken || undefined }
                : undefined,
        };
        sessionStorage.setItem('analysisState', JSON.stringify(analysisState));
        navigate('/analyse/view');
    };

    if (!authLoaded) return <div className="text-secondary text-center py-5">Loading...</div>;

    const hasSubject = filters.mode === 'team' ? !!filters.teamId : !!filters.playerSteamId;
    const subjectName = filters.mode === 'team' ? selectedTeam?.teamName : selectedPlayerInfo?.playerName;
    const teamBuyLabel = filters.mode === 'team'
        ? (selectedTeam?.teamName || 'Team')
        : (selectedPlayerInfo?.playerName ? `${selectedPlayerInfo.playerName}'s team` : 'Team');

    // Where the "from …" banner points back to, and which map it names. An upload's map is
    // fixed by the demo; a league match's rides in on the link.
    const backHref = isUpload
        ? (seed.current.shareToken ? `/shared-demo/${seed.current.shareToken}` : `/demo/${seed.current.uploadId}`)
        : `/match/${seed.current.matchId}`;
    const bannerMap = isUpload ? filters.mapName : seed.current.mapName;

    // "1 demo" / "3 demos" for uploads, "1 match" / "3 matches" for league.
    const countLabel = (n: number) => isUpload
        ? `${n} demo${n === 1 ? '' : 's'}`
        : `${n} match${n === 1 ? '' : 'es'}`;

    return (
        <div className={styles.modernPage}>
            {/* Header */}
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Round Analysis</h1>
                <p className={styles.pageSubtitle}>
                    {isUpload
                        ? 'Filter the rounds of your own uploaded demo, then break them down side-by-side.'
                        : 'Filter PowerLigaen rounds across matches, then break them down side-by-side.'}
                </p>
            </div>

            {/* Came from a match: offer both of its sides, and a way back. */}
            {fromMatch && fromMatch[0].team1 && fromMatch[0].team2 && (
                <div className={styles.fromMatch}>
                    <div className={styles.fromMatchHead}>
                        <span className={styles.fromMatchLabel}>{isUpload ? 'From your demo' : 'From match'}</span>
                        <Link to={backHref} className={styles.fromMatchBack}>
                            <ArrowBackIcon style={{ fontSize: 15 }} />
                            {isUpload ? 'Back to demo' : 'Back to match'}
                        </Link>
                    </div>
                    <div className={styles.fromMatchBody}>
                        <span className={styles.fromMatchScore}>
                            {fromMatch.length > 1
                                ? `${fromMatch.length} maps`
                                : `${fromMatch[0].team1score} : ${fromMatch[0].team2score}`}
                            {bannerMap && ` · ${mapMappings.get(bannerMap) ?? bannerMap}`}
                            {isUpload ? ' · Private demo' : ` · S${fromMatch[0].season}`}
                        </span>
                        <span className={styles.fromMatchPrompt}>Analyse which side?</span>
                        <div className={styles.fromMatchTeams}>
                            {[fromMatch[0].team1, fromMatch[0].team2].map(team => (
                                <button
                                    key={team.teamId}
                                    className={`${styles.fromMatchTeam} ${filters.teamId === team.teamId ? styles.fromMatchTeamActive : ''}`}
                                    onClick={() => pickMatchTeam(team)}
                                >
                                    {team.teamLogo
                                        ? <img src={team.teamLogo} alt="" />
                                        : <GroupsIcon style={{ fontSize: 16, opacity: 0.6 }} />}
                                    {team.teamName}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ① Subject — in upload mode the banner above IS the subject picker: one demo,
                two sides, a fixed season and a fixed map. */}
            {!isUpload && (
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.cardIcon}>
                            {filters.mode === 'team' ? <GroupsIcon /> : <PersonIcon />}
                        </span>
                        Subject
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {filters.mode === 'team' && selectedTeam && (
                            <div className={styles.subjectPreview}>
                                <div className={styles.subjectPreviewLogo}>
                                    {selectedTeam.teamLogo
                                        ? <img src={selectedTeam.teamLogo} alt="" />
                                        : <GroupsIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }} />}
                                </div>
                                <div className={styles.subjectPreviewText}>
                                    <span className={styles.subjectPreviewName}>{selectedTeam.teamName}</span>
                                    <span className={styles.subjectPreviewMeta}>
                                        {filters.seasons.length > 0
                                            ? filters.seasons.sort((a, b) => b - a).map(s => `S${s}`).join(' · ')
                                            : 'No seasons'}
                                    </span>
                                </div>
                            </div>
                        )}
                        {filters.mode === 'player' && selectedPlayerInfo && (
                            <div className={styles.subjectPreview}>
                                <div className={styles.subjectPreviewLogo}>
                                    <PlayerImage id={selectedPlayerInfo.steamId} width={28} />
                                </div>
                                <div className={styles.subjectPreviewText}>
                                    <span className={styles.subjectPreviewName}>{selectedPlayerInfo.playerName}</span>
                                    <span className={styles.subjectPreviewMeta}>
                                        {filters.seasons.length > 0
                                            ? filters.seasons.sort((a, b) => b - a).map(s => `S${s}`).join(' · ')
                                            : 'No seasons'}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className={styles.modeTabs}>
                            <button
                                className={`${styles.modeTab} ${filters.mode === 'team' ? styles.modeTabActive : ''}`}
                                onClick={() => setMode('team')}
                            >By Team</button>
                            <button
                                className={`${styles.modeTab} ${filters.mode === 'player' ? styles.modeTabActive : ''}`}
                                onClick={() => setMode('player')}
                            >By Player</button>
                        </div>
                    </div>
                </div>

                <div className={styles.subjectGrid}>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            {filters.mode === 'team' ? 'Team' : 'Player'}
                        </label>
                        {filters.mode === 'team' ? (
                            <div className={styles.modernSearchDropdown} ref={teamDropdownRef}>
                                <input
                                    type="text"
                                    className={styles.modernInput}
                                    placeholder="Search team…"
                                    value={teamDropdownOpen ? teamSearch : (selectedTeam?.teamName || teamSearch)}
                                    onChange={e => { setTeamSearch(e.target.value); setTeamDropdownOpen(true); }}
                                    onFocus={() => setTeamDropdownOpen(true)}
                                />
                                {teamDropdownOpen && (
                                    <div className={styles.modernDropdownList}>
                                        {filteredTeams.map(t => (
                                            <div
                                                key={t.teamId}
                                                className={`${styles.modernDropdownItem} ${t.teamId === filters.teamId ? styles.active : ''}`}
                                                onClick={() => {
                                                    setFilters(prev => ({ ...prev, teamId: t.teamId }));
                                                    setTeamSearch('');
                                                    setTeamDropdownOpen(false);
                                                }}
                                            >
                                                {t.teamName}
                                            </div>
                                        ))}
                                        {filteredTeams.length === 0 && (
                                            <div className={styles.modernDropdownEmpty}>No teams found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles.modernSearchDropdown} ref={playerDropdownRef}>
                                <input
                                    type="text"
                                    className={styles.modernInput}
                                    placeholder="Search player…"
                                    value={playerDropdownOpen ? playerSearch : (selectedPlayerInfo?.playerName || playerSearch)}
                                    onChange={e => { setPlayerSearch(e.target.value); setPlayerDropdownOpen(true); }}
                                    onFocus={() => setPlayerDropdownOpen(true)}
                                />
                                {playerDropdownOpen && (
                                    <div className={styles.modernDropdownList}>
                                        {playerResults.map(p => (
                                            <div
                                                key={p.steamId}
                                                className={`${styles.modernDropdownItem} ${p.steamId === filters.playerSteamId ? styles.active : ''}`}
                                                onClick={() => {
                                                    setFilters(prev => ({ ...prev, playerSteamId: p.steamId }));
                                                    setSelectedPlayerInfo(p);
                                                    setPlayerSearch('');
                                                    setPlayerDropdownOpen(false);
                                                }}
                                            >
                                                {p.playerName}
                                            </div>
                                        ))}
                                        {playerSearch.length < 2 && (
                                            <div className={styles.modernDropdownEmpty}>Type at least 2 characters</div>
                                        )}
                                        {playerSearch.length >= 2 && playerResults.length === 0 && (
                                            <div className={styles.modernDropdownEmpty}>No players found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Seasons</label>
                        <div className={styles.chipRow}>
                            {Array.from({ length: 10 }, (_, i) => currentSeason - i).map(s => {
                                const active = filters.seasons.includes(s);
                                return (
                                    <button
                                        key={s}
                                        className={`${styles.chip} ${active ? styles.chipActive : ''}`}
                                        onClick={() => setFilters(prev => {
                                            const next = active
                                                ? prev.seasons.filter(x => x !== s)
                                                : [...prev.seasons, s].sort((a, b) => b - a);
                                            return { ...prev, seasons: next };
                                        })}
                                    >
                                        S{s}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* ② Map — in upload mode the counts come from my own demos, so the tiles show
                which maps I have uploads on for this team. */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.cardIcon}><MapIcon /></span>
                        Map
                    </h2>
                    {hasSubject && filters.mapName && (
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                            {mapMappings.get(filters.mapName)} · {countLabel(mapCounts[filters.mapName] ?? 0)}
                        </span>
                    )}
                </div>

                <div className={styles.mapTileGrid}>
                    {MAPS.map(([key, label]) => {
                        const count = mapCounts[key] ?? 0;
                        const isActive = filters.mapName === key;
                        const isEmpty = hasSubject && count === 0;
                        // Once a subject is loaded, only render maps the team/player
                        // has actually played in the selected seasons — derives the
                        // effective map pool from the data instead of a hardcoded list.
                        if (isEmpty && !isActive) return null;
                        const thumb = mapPhotoMappings[key];
                        return (
                            <button
                                key={key}
                                className={`${styles.mapTile} ${isActive ? styles.mapTileActive : ''} ${isEmpty ? styles.empty : ''}`}
                                onClick={() => setFilters(prev => ({ ...prev, mapName: key }))}
                                disabled={isEmpty && !isActive}
                                title={`${label}${hasSubject ? ` · ${countLabel(count)}` : ''}`}
                            >
                                {thumb && <img className={styles.mapTileImage} src={thumb} alt="" />}
                                <span className={styles.mapTileOverlay} />
                                <span className={styles.mapTileLabel}>{label}</span>
                                {hasSubject && (
                                    <span className={styles.mapTileCount}>{count}</span>
                                )}
                            </button>
                        );
                    })}
                    {hasSubject && Object.values(mapCounts).every(c => c === 0) && (
                        <div style={{
                            gridColumn: '1 / -1',
                            padding: 24,
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: '0.9rem',
                        }}>
                            {isUpload
                                ? 'No uploaded demos with this team yet.'
                                : 'No matches on any map yet for the selected seasons.'}
                        </div>
                    )}
                </div>
            </div>

            {/* ③ Round filters */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.cardIcon}><TuneIcon /></span>
                        Round Filters
                    </h2>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.filterRow}>
                        <span className={styles.filterRowLabel}>Side</span>
                        <div className={styles.chipRow}>
                            {(['all', 'T', 'CT'] as const).map(s => (
                                <button
                                    key={s}
                                    className={`${styles.chip} ${filters.side === s ? styles.chipActive : ''}`}
                                    onClick={() => setFilters(prev => ({ ...prev, side: s }))}
                                >
                                    {s === 'all' ? 'Both' : s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.filterRow}>
                        <span className={styles.filterRowLabel}>Outcome</span>
                        <div className={styles.chipRow}>
                            {(['all', 'W', 'L'] as const).map(o => (
                                <button
                                    key={o}
                                    className={`${styles.chip} ${filters.outcome === o ? styles.chipActive : ''}`}
                                    onClick={() => setFilters(prev => ({ ...prev, outcome: o }))}
                                >
                                    {o === 'all' ? 'Both' : o === 'W' ? 'Win' : 'Loss'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.filterRow}>
                        <span className={styles.filterRowLabel} title={teamBuyLabel}>
                            {filters.mode === 'team' ? 'Team buy' : 'Player buy'}
                        </span>
                        <div className={styles.chipRow}>
                            {BUY_TYPES.map(bt => (
                                <button
                                    key={bt.id}
                                    className={`${styles.chip} ${filters.teamBuyTypes.has(bt.id) ? styles.chipActive : ''}`}
                                    onClick={() => toggleBuyType('team', bt.id)}
                                >
                                    {bt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.filterRow}>
                        <span className={styles.filterRowLabel}>
                            Opponent buy
                            <span className={styles.labelHint} title="What the opposing team bought that round">
                                <HelpOutlineIcon />
                            </span>
                        </span>
                        <div className={styles.chipRow}>
                            {BUY_TYPES.map(bt => (
                                <button
                                    key={bt.id}
                                    className={`${styles.chip} ${filters.opponentBuyTypes.has(bt.id) ? styles.chipActive : ''}`}
                                    onClick={() => toggleBuyType('opponent', bt.id)}
                                >
                                    {bt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ④ Matches */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.cardIcon}><EventNoteIcon /></span>
                        Matches
                    </h2>
                    {matches.length > 0 && (
                        <label className={styles.selectAllToggle} onClick={toggleAllMatches}>
                            <input
                                type="checkbox"
                                checked={(() => {
                                    const selectableCount = matches.filter(m => m.hasReplay && (filters.mode === 'team' || m.playerTeamId)).length;
                                    return selectableCount > 0 && selectedMatchIds.size === selectableCount;
                                })()}
                                readOnly
                            />
                            {selectedMatchIds.size > 0 ? 'Unselect all' : 'Select all'}
                        </label>
                    )}
                </div>

                {!hasSubject || !filters.mapName ? (
                    <div className={styles.matchHint}>
                        {!hasSubject
                            ? `Pick a ${filters.mode === 'team' ? 'team' : 'player'} above to load matches.`
                            : 'Pick a map above to load matches.'}
                    </div>
                ) : loading ? (
                    <div className={styles.matchEmpty}>Loading matches…</div>
                ) : matches.length === 0 ? (
                    <div className={styles.matchEmpty}>No matches found for this filter.</div>
                ) : (
                    <>
                        <div className={styles.matchListHeader}>
                            <span>
                                {subjectName
                                    ? `${countLabel(matches.length)} found for ${subjectName}`
                                    : countLabel(matches.length)}
                                {' · '}
                                <span style={{ color: selectedMatchIds.size >= maxGames ? '#ff9933' : 'rgba(255,255,255,0.6)' }}>
                                    {selectedMatchIds.size}/{maxGames} selected
                                </span>
                            </span>
                            <span>Rounds</span>
                        </div>
                        {!isDonator && (
                            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', padding: '4px 0 8px' }}>
                                Free preview compares up to {maxGames} games at once.{' '}
                                <Link to="/premium" style={{ color: '#ff9933', fontWeight: 600 }}>Donators can compare up to 100 →</Link>
                            </div>
                        )}
                        <div className={styles.modernMatchList}>
                            {matches.map(match => {
                                const perspectiveTeamId = getPerspectiveTeamId(match);
                                const hasPerspective = !!perspectiveTeamId;
                                const isTeam1 = match.team1Id === perspectiveTeamId;
                                const opponent = isTeam1 ? match.team2Name : match.team1Name;
                                const teamScore = isTeam1 ? match.team1score : match.team2score;
                                const oppScore = isTeam1 ? match.team2score : match.team1score;
                                const date = parseMatchTime(match.matchTime).toLocaleDateString();
                                const matchRounds = roundsPerMatch[match.gameid] || 0;
                                const selectable = match.hasReplay && (filters.mode === 'team' || hasPerspective);
                                const isSelected = selectedMatchIds.has(match.gameid);

                                const playerTeamName = filters.mode === 'player' && hasPerspective
                                    ? (isTeam1 ? match.team1Name : match.team2Name)
                                    : null;

                                return (
                                    <div
                                        key={match.gameid}
                                        className={`${styles.modernMatchItem} ${!selectable ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
                                        onClick={() => selectable && toggleMatch(match.gameid)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={!selectable}
                                            readOnly
                                        />
                                        <span className={styles.matchDateModern}>{date}</span>
                                        <span className={styles.matchScoreModern}>
                                            {hasPerspective ? `${teamScore} – ${oppScore}` : `${match.team1score} – ${match.team2score}`}
                                        </span>
                                        <span className={styles.matchOpp}>
                                            {playerTeamName && (
                                                <span className={styles.matchTeamSub}>({playerTeamName})</span>
                                            )}
                                            {hasPerspective ? `vs ${opponent}` : `${match.team1Name} vs ${match.team2Name}`}
                                        </span>
                                        {!match.hasReplay ? (
                                            <span className={`${styles.miniBadge} ${styles.danger}`}>No replay</span>
                                        ) : !hasPerspective && filters.mode === 'player' ? (
                                            <span className={`${styles.miniBadge} ${styles.warn}`}>No team data</span>
                                        ) : (
                                            <span />
                                        )}
                                        <span className={`${styles.matchRoundsBadge} ${matchRounds === 0 ? styles.zero : ''}`}>
                                            {matchRounds}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Sticky action bar */}
            <div className={`${styles.actionBar} ${filteredRounds.length > 0 ? styles.actionBarReady : ''}`}>
                <div>
                    <span className={styles.actionRoundsCount}>{filteredRounds.length}</span>
                    <span className={styles.actionRoundsLabel}>
                        {filteredRounds.length === 1 ? 'round selected' : 'rounds selected'}
                    </span>
                </div>
                <button
                    className={styles.actionBtn}
                    onClick={handleAnalyse}
                    disabled={filteredRounds.length === 0}
                >
                    Analyse
                    <span className={styles.actionArrow}><ArrowForwardIcon /></span>
                </button>
            </div>
        </div>
    );
};

export default AnalysisFilterPage;
